import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Empty,
  Input,
  InputNumber,
  Modal,
  Progress,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { CloudDeck } from '@lingoping/core';
import {
  AdminApiError,
  MAX_WORDS_PER_DECK,
  errorMessage,
  type AdminApi,
  type AdminDeckWord,
} from '../api';
import { WordList } from '../components/WordList';
import { runPrepare, type PrepareOutcome, type PrepareProgress } from '../lib/prepare';
import { newKey, parseWordList, type EditorWord, type InvalidLine } from '../lib/words';
import { CardPreviewModal } from '../components/CardPreview';
import type { Route } from '../router';

function toEditorWords(words: AdminDeckWord[]): EditorWord[] {
  return words
    .slice()
    .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id))
    .map((w) => ({ key: w.id, lemma: w.lemma, context: w.context ?? '', ready: w.ready }));
}

const wordsSignature = (words: EditorWord[]) =>
  JSON.stringify(words.map((w) => [w.lemma, w.context]));

interface PrepState {
  running: boolean;
  total: number;
  progress: PrepareProgress | null;
  outcome: PrepareOutcome | null;
}

const IDLE_PREP: PrepState = { running: false, total: 0, progress: null, outcome: null };

export const DeckEditorScreen: React.FC<{
  api: AdminApi;
  deckId: string;
  navigate: (route: Route) => void;
}> = ({ api, deckId, navigate }) => {
  const { message } = AntApp.useApp();

  const [deck, setDeck] = useState<CloudDeck | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [group, setGroup] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [words, setWords] = useState<EditorWord[]>([]);
  /** The word whose card is open in the preview. */
  const [viewing, setViewing] = useState<string | null>(null);
  const [savedWordsSig, setSavedWordsSig] = useState('[]');

  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [prep, setPrep] = useState<PrepState>(IDLE_PREP);
  const cancelPrep = useRef(false);

  const [addOpen, setAddOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteProblems, setPasteProblems] = useState<InvalidLine[]>([]);

  const apply = useCallback((d: CloudDeck, serverWords: AdminDeckWord[]) => {
    const editorWords = toEditorWords(serverWords);
    setDeck(d);
    setTitle(d.title);
    setGroup(d.group_name ?? '');
    setDescription(d.description ?? '');
    setSortOrder(d.sort_order ?? 0);
    setWords(editorWords);
    setSavedWordsSig(wordsSignature(editorWords));
  }, []);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .getDeck(deckId)
      .then((r) => {
        if (cancelled) return;
        apply(r.deck, r.words);
        setLoadError(null);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [api, deckId, apply, reloadKey]);

  const metaChanged =
    deck !== null &&
    (title.trim() !== deck.title ||
      group.trim() !== (deck.group_name ?? '') ||
      description.trim() !== (deck.description ?? '') ||
      sortOrder !== (deck.sort_order ?? 0));
  const wordsChanged = wordsSignature(words) !== savedWordsSig;
  const dirty = metaChanged || wordsChanged;

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const missing = words.filter((w) => w.ready === false).length;
  const readyCount = words.filter((w) => w.ready === true).length;
  const allReady = words.length > 0 && words.every((w) => w.ready === true);
  const busy = saving || publishing || prep.running;
  const published = deck?.status === 'published';

  const save = async () => {
    if (!deck) return;
    if (!title.trim()) {
      message.error('The deck needs a name');
      return;
    }
    setSaving(true);
    try {
      if (metaChanged) {
        const fields: Parameters<AdminApi['updateDeck']>[1] = {};
        if (title.trim() !== deck.title) fields.title = title.trim();
        if (group.trim() !== (deck.group_name ?? '')) fields.group_name = group.trim() || null;
        if (description.trim() !== (deck.description ?? ''))
          fields.description = description.trim();
        if (sortOrder !== (deck.sort_order ?? 0)) fields.sort_order = sortOrder;
        await api.updateDeck(deck.id, fields);
      }
      let summary = '';
      if (wordsChanged) {
        const r = await api.setWords(
          deck.id,
          words.map((w) => ({ lemma: w.lemma, context: w.context || undefined }))
        );
        summary = ` (${r.added} added, ${r.removed} removed)`;
      }
      const fresh = await api.getDeck(deck.id);
      apply(fresh.deck, fresh.words);
      message.success(`Saved${summary}`);
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const addWords = () => {
    const parsed = parseWordList(
      pasteText,
      words.map((w) => w.lemma)
    );
    if (words.length + parsed.words.length > MAX_WORDS_PER_DECK) {
      message.error(`A deck can hold at most ${MAX_WORDS_PER_DECK.toLocaleString('en')} words`);
      return;
    }
    if (parsed.words.length) {
      setWords((prev) => [
        ...prev,
        ...parsed.words.map((w) => ({ key: newKey(), lemma: w.lemma, context: w.context })),
      ]);
    }
    const skipped = parsed.duplicates.length
      ? `, ${parsed.duplicates.length} duplicate${parsed.duplicates.length === 1 ? '' : 's'} skipped`
      : '';
    const added = `${parsed.words.length} word${parsed.words.length === 1 ? '' : 's'} added${skipped}.`;
    if (parsed.invalid.length) {
      // Keep the window open with only the lines that could not be used, so they can be fixed.
      setPasteProblems(parsed.invalid);
      setPasteText(parsed.invalid.map((l) => l.text).join('\n'));
      if (parsed.words.length) message.info(`${added} Save to keep them.`);
      return;
    }
    closeAddWords();
    message.info(`${added} Save to keep them.`);
  };

  const closeAddWords = () => {
    setAddOpen(false);
    setPasteText('');
    setPasteProblems([]);
  };

  const prepareMissing = async () => {
    if (!deck) return;
    cancelPrep.current = false;
    setPrep({ running: true, total: missing, progress: null, outcome: null });
    const outcome = await runPrepare(api, deck.id, {
      onProgress: (progress) => setPrep((p) => ({ ...p, progress })),
      isCancelled: () => cancelPrep.current,
    });
    setPrep((p) => ({ ...p, running: false, outcome }));
    try {
      const fresh = await api.getDeck(deck.id);
      apply(fresh.deck, fresh.words);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const setStatus = async (action: 'publish' | 'unpublish') => {
    if (!deck) return;
    setPublishing(true);
    try {
      const updated =
        action === 'publish' ? await api.publish(deck.id) : await api.unpublish(deck.id);
      setDeck(updated);
      message.success(
        action === 'publish' ? 'Published: learners can see this deck now' : 'Unpublished'
      );
    } catch (e) {
      message.error(errorMessage(e));
      if (e instanceof AdminApiError && e.code === 'not_ready') {
        // The list changed under us: show the real state.
        try {
          const fresh = await api.getDeck(deck.id);
          setDeck(fresh.deck);
          setWords((prev) =>
            wordsSignature(prev) === savedWordsSig ? toEditorWords(fresh.words) : prev
          );
        } catch {
          /* the error above already told the user */
        }
      }
    } finally {
      setPublishing(false);
    }
  };

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title="Could not load the deck"
        description={loadError}
        action={
          <Space>
            <Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>
            <Button onClick={() => navigate({ name: 'decks' })}>Back to decks</Button>
          </Space>
        }
      />
    );
  }
  if (!deck) return <Skeleton active paragraph={{ rows: 8 }} />;

  const publishBlockedReason = dirty
    ? 'Save your changes first'
    : words.length === 0
      ? 'Add words first'
      : !allReady
        ? `${missing} word${missing === 1 ? '' : 's'} still need${missing === 1 ? 's' : ''} a card`
        : '';

  const prepPercent =
    prep.total > 0 && prep.progress
      ? Math.min(100, Math.round((prep.progress.prepared / prep.total) * 100))
      : 0;

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate({ name: 'decks' })}
        style={{ paddingLeft: 0 }}
      >
        Public decks
      </Button>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          margin: '4px 0 16px',
        }}
      >
        <Space align="center" size={12}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {deck.title}
          </Typography.Title>
          {published ? <Tag color="green">Published</Tag> : <Tag>Draft</Tag>}
        </Space>
        <Space wrap>
          <Button
            type="primary"
            onClick={() => void save()}
            loading={saving}
            disabled={!dirty || prep.running || publishing}
          >
            Save
          </Button>
          {published ? (
            <Button
              onClick={() => void setStatus('unpublish')}
              loading={publishing}
              disabled={saving || prep.running}
            >
              Unpublish
            </Button>
          ) : (
            <Tooltip title={publishBlockedReason}>
              <span>
                <Button
                  onClick={() => void setStatus('publish')}
                  loading={publishing}
                  disabled={Boolean(publishBlockedReason) || busy}
                >
                  Publish
                </Button>
              </span>
            </Tooltip>
          )}
        </Space>
      </div>

      {published && (
        <Alert
          type="info"
          showIcon
          title="This deck is live. Saved changes reach learners right away."
          style={{ marginBottom: 16 }}
        />
      )}

      <Card title="Details" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
          }}
        >
          <label>
            <Typography.Text strong>Name</Typography.Text>
            <Input
              aria-label="Name"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
            />
          </label>
          <label>
            <Typography.Text strong>Group</Typography.Text>
            <Input
              aria-label="Group"
              value={group}
              maxLength={80}
              placeholder="Life in Germany"
              onChange={(e) => setGroup(e.target.value)}
              disabled={busy}
            />
          </label>
          <label>
            <Typography.Text strong>Sort order</Typography.Text>
            <InputNumber
              aria-label="Sort order"
              value={sortOrder}
              precision={0}
              onChange={(v) => setSortOrder(typeof v === 'number' ? v : 0)}
              disabled={busy}
              style={{ width: '100%' }}
            />
          </label>
        </div>
        <label style={{ display: 'block', marginTop: 16 }}>
          <Typography.Text strong>Description</Typography.Text>
          <Input.TextArea
            aria-label="Description"
            rows={2}
            value={description}
            maxLength={500}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
          />
        </label>
      </Card>

      <Card
        title={`Words (${words.length})`}
        extra={
          <Space wrap>
            <Typography.Text type="secondary">
              Cards ready: {readyCount} of {words.length}
            </Typography.Text>
            <Button icon={<PlusOutlined />} onClick={() => setAddOpen(true)} disabled={busy}>
              Add words
            </Button>
            <Tooltip title={dirty ? 'Save your changes first' : ''}>
              <span>
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={() => void prepareMissing()}
                  disabled={dirty || missing === 0 || busy}
                  loading={prep.running}
                >
                  {`Prepare ${missing} missing card${missing === 1 ? '' : 's'}`}
                </Button>
              </span>
            </Tooltip>
          </Space>
        }
      >
        {prep.running && (
          <div style={{ marginBottom: 16 }}>
            <Progress percent={prepPercent} status="active" />
            <Space>
              <Typography.Text>
                {prep.progress
                  ? `Prepared ${prep.progress.prepared} of ${prep.total}, ${prep.progress.remaining} to go…`
                  : `Preparing ${prep.total} cards…`}
              </Typography.Text>
              <Button size="small" onClick={() => (cancelPrep.current = true)}>
                Stop after this batch
              </Button>
            </Space>
          </div>
        )}

        {!prep.running && prep.outcome && <PrepareReport outcome={prep.outcome} />}

        {words.length === 0 ? (
          <Empty description="No words yet. Add words by pasting a list." />
        ) : (
          <WordList
            words={words}
            onChange={setWords}
            disabled={busy}
            onView={(w) => setViewing(w.lemma)}
          />
        )}
      </Card>

      <CardPreviewModal
        source={
          viewing
            ? { key: viewing, lemma: viewing, load: () => api.getCard(deckId, viewing) }
            : null
        }
        onClose={() => setViewing(null)}
      />

      <Modal
        title="Add words"
        open={addOpen}
        okText="Add to list"
        okButtonProps={{ disabled: !pasteText.trim() }}
        onOk={addWords}
        onCancel={closeAddWords}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          One word or phrase per line. To keep the sentence a word came from, put it after a tab:{' '}
          <Typography.Text code>Schiff⇥Das Schiff fährt nach Hamburg.</Typography.Text> Words
          already in the deck are skipped.
        </Typography.Paragraph>
        {pasteProblems.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            title={
              pasteProblems.length === 1
                ? 'This line could not be added. Fix it below, or remove it.'
                : `These ${pasteProblems.length} lines could not be added. Fix them below, or remove them.`
            }
            description={
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {pasteProblems.map((p) => (
                  <li key={`${p.line}-${p.text}`}>
                    <Typography.Text strong>{p.lemma || '(empty)'}</Typography.Text>: {p.reason}
                  </li>
                ))}
              </ul>
            }
          />
        )}
        <Input.TextArea
          aria-label="Words to add"
          rows={10}
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder={'Schiff\nBahnhof\nEs tut mir leid'}
          autoFocus
        />
      </Modal>
    </div>
  );
};

const PrepareReport: React.FC<{ outcome: PrepareOutcome }> = ({ outcome }) => {
  const { stopped, prepared, remaining, failed, error } = outcome;
  const style = { marginBottom: 16 };
  if (stopped === 'done') {
    return (
      <Alert
        type="success"
        showIcon
        style={style}
        title={`All cards are ready (${prepared} prepared just now).`}
      />
    );
  }
  if (stopped === 'cancelled') {
    return (
      <Alert
        type="info"
        showIcon
        style={style}
        title={`Stopped. ${prepared} prepared, ${remaining} still to do.`}
      />
    );
  }
  return (
    <Alert
      type={stopped === 'error' ? 'error' : 'warning'}
      showIcon
      style={style}
      title={
        stopped === 'error'
          ? `Preparing stopped: ${errorMessage(error)} (${prepared} prepared, ${remaining} still to do)`
          : `${remaining} word${remaining === 1 ? '' : 's'} could not be prepared (${prepared} prepared).`
      }
      description={
        failed.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {failed.map((f) => (
              <li key={f.lemma}>
                <Typography.Text strong>{f.lemma}</Typography.Text>: {f.error}
              </li>
            ))}
          </ul>
        ) : undefined
      }
    />
  );
};
